import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Sparkles, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errorMessage, useApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDate } from "@/lib/format";
import { CATEGORY_LABELS, markSeen } from "@/lib/changelog";
import type { ChangelogEntry } from "@/lib/types";
import { EmptyState, Loading } from "@/components/app/PageHeader";
import { ConfirmAction } from "@/components/app/ConfirmAction";

type Form = Pick<ChangelogEntry, "title" | "body" | "category" | "version">;
const EMPTY: Form = { title: "", body: "", category: "new", version: "" };

function EntryDialog({ entry, open, onOpenChange, onDone }: { entry: ChangelogEntry | null; open: boolean; onOpenChange: (o: boolean) => void; onDone: () => void }) {
  const [form, setForm] = useState<Form>(EMPTY);
  useEffect(() => { if (open) setForm(entry ? { title: entry.title, body: entry.body, category: entry.category, version: entry.version } : EMPTY); }, [open, entry]);
  const submit = async () => {
    try {
      if (entry) await api.put(`/admin/changelog/${entry.id}`, form); else await api.post("/admin/changelog", form);
      toast.success(entry ? "Nouveauté modifiée" : "Nouveauté publiée");
      onOpenChange(false); onDone();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="changelog-dialog" className="border-slate-800 bg-slate-900">
        <DialogHeader><DialogTitle>{entry ? "Modifier la nouveauté" : "Publier une nouveauté"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label>Titre</Label><Input data-testid="changelog-title-input" value={form.title} maxLength={140} onChange={(e) => setForm({ ...form, title: e.target.value })} className="border-slate-700 bg-slate-950/60" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Catégorie</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v as any })}>
                <SelectTrigger data-testid="changelog-category-select" className="border-slate-700 bg-slate-950/60"><SelectValue /></SelectTrigger>
                <SelectContent className="border-slate-800 bg-slate-900">{Object.entries(CATEGORY_LABELS).map(([k, c]) => <SelectItem key={k} value={k}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Version (optionnel)</Label><Input data-testid="changelog-version-input" value={form.version} maxLength={30} onChange={(e) => setForm({ ...form, version: e.target.value })} placeholder="1.5" className="border-slate-700 bg-slate-950/60" /></div>
          </div>
          <div className="space-y-2"><Label>Description</Label><Textarea data-testid="changelog-body-input" rows={5} value={form.body} maxLength={5000} onChange={(e) => setForm({ ...form, body: e.target.value })} className="border-slate-700 bg-slate-950/60" /></div>
          <Button data-testid="changelog-submit" disabled={form.title.trim().length < 2 || !form.body.trim()} onClick={submit} className="w-full bg-blue-600 text-white hover:bg-blue-500">{entry ? "Enregistrer" : "Publier"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ChangelogPanel() {
  const { user } = useAuth();
  const isSuper = !!user && user.role === "super_admin";
  const { data, loading, reload } = useApi<ChangelogEntry[]>("/changelog");
  const [editing, setEditing] = useState<ChangelogEntry | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => { if (user && data?.length) markSeen(user.id, data[0].created_at); }, [user, data]);

  const remove = async (e: ChangelogEntry) => {
    try { await api.delete(`/admin/changelog/${e.id}`); toast.success("Nouveauté supprimée"); reload(); } catch (err) { toast.error(errorMessage(err)); }
  };

  if (loading && !data) return <Loading />;
  return (
    <div className="space-y-4" data-testid="changelog-panel">
      {isSuper && <div className="flex justify-end"><Button data-testid="btn-new-changelog" onClick={() => { setEditing(null); setOpen(true); }} className="bg-blue-600 text-white hover:bg-blue-500"><Plus className="mr-1 h-4 w-4" />Publier une nouveauté</Button></div>}
      {!data?.length && <EmptyState icon={<Sparkles className="h-5 w-5" />} title="Aucune nouveauté" text="Les nouvelles fonctions de la plateforme apparaîtront ici." testId="changelog-empty" />}
      <ol className="relative space-y-4 border-l border-slate-800 pl-6">
        {(data || []).map((e) => (
          <li key={e.id} data-testid={`changelog-entry-${e.id}`} className="panel relative p-5">
            <span className="absolute -left-[31px] top-6 h-3 w-3 rounded-full border-2 border-slate-950 bg-orange-400" />
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${CATEGORY_LABELS[e.category].cls}`}>{CATEGORY_LABELS[e.category].label}</span>
              {e.version && <span className="font-mono text-xs text-slate-400">v{e.version}</span>}
              <span className="text-xs text-slate-500">{fmtDate(e.created_at)}</span>
              {isSuper && (
                <div className="ml-auto flex gap-1">
                  <Button variant="ghost" size="icon" data-testid={`changelog-edit-${e.id}`} onClick={() => { setEditing(e); setOpen(true); }} className="h-8 w-8 text-slate-400 hover:text-slate-100"><Pencil className="h-4 w-4" /></Button>
                  <ConfirmAction title="Supprimer cette nouveauté ?" description={e.title} confirmLabel="Supprimer" onConfirm={() => remove(e)} testId={`changelog-delete-confirm-${e.id}`}
                    trigger={<Button variant="ghost" size="icon" data-testid={`changelog-delete-${e.id}`} className="h-8 w-8 text-slate-400 hover:text-rose-400"><Trash2 className="h-4 w-4" /></Button>} />
                </div>
              )}
            </div>
            <h3 className="mt-3 text-lg font-medium text-slate-100">{e.title}</h3>
            <p className="mt-1 whitespace-pre-line text-sm text-slate-400">{e.body}</p>
          </li>
        ))}
      </ol>
      {isSuper && <EntryDialog entry={editing} open={open} onOpenChange={setOpen} onDone={reload} />}
    </div>
  );
}
